import type { GetEventRow, GetEventsRow } from '@morfi/backend-sdk-v2/client';
import type { EventCalendarEvent } from '@/components/live/event-calendar';
import type { LiveEventHeroDisplay } from '@/components/live/event-page/live-event-detail-page';
import type { LiveEventsListItem } from '@/components/live/feed/live-events-list';
import type { LiveHeroEvent } from '@/components/live/live-hero';
import type {
  LiveEventCategoryDisplay,
  LiveEventCategoryIconName,
  LiveEventPhase,
} from '@/components/live/types';

export type PublishedEvent = GetEventRow | GetEventsRow;

function categoryIconName(label: string): LiveEventCategoryIconName {
  switch (label.toLowerCase()) {
    case 'sports':
      return 'sports';
    case 'politics':
      return 'politics';
    case 'technology':
      return 'technology';
    case 'entertainment':
      return 'entertainment';
    default:
      return 'calendar';
  }
}

export function eventPhase(
  event: PublishedEvent,
  nowMs: number,
): Exclude<LiveEventPhase, 'no-markets-open'> {
  if (nowMs < Date.parse(event.tradingStartAt)) return 'upcoming';
  if (nowMs < Date.parse(event.tradingEndAt)) return 'live';
  return 'concluded';
}

export function mapPublishedEvent(
  event: PublishedEvent,
  nowMs: number,
): {
  readonly event: LiveHeroEvent & { category: LiveEventCategoryDisplay };
  readonly card: LiveEventsListItem;
  readonly calendar: EventCalendarEvent;
  readonly detailHero: LiveEventHeroDisplay;
} {
  const category = {
    label: event.category,
    iconName: categoryIconName(event.category),
  };
  const participantLogos =
    event.identityMode === 'participant_pair' &&
    event.participants.length === 2 &&
    event.participants.every((participant) => participant.image !== null)
      ? event.participants.flatMap((participant) =>
          participant.image === null
            ? []
            : [{ src: participant.image.url, alt: participant.image.alt ?? participant.name }],
        )
      : null;
  const summary = {
    id: event.eventId,
    title: event.title,
    phase: eventPhase(event, nowMs),
    start: event.tradingStartAt,
    end: event.tradingEndAt,
    bannerImageUrl: event.bannerImage.url,
    eventLogo: { src: event.eventLogo.url, alt: event.eventLogo.alt ?? event.title },
    participantLogos,
    category,
  };

  return {
    event: summary,
    card: { event: summary, metrics: [], initialNowMs: nowMs },
    calendar: {
      id: event.eventId,
      title: event.title,
      category: event.category,
      phase: summary.phase,
      start: event.tradingStartAt,
      end: event.tradingEndAt,
      imageUrl: event.bannerImage.url,
      featured: event.featured,
    },
    detailHero: {
      event: { ...summary, subtitle: event.subtitle },
      metrics: [],
      initialNowMs: nowMs,
      timeZone: event.timezone,
    },
  };
}
